// Обёртка над Telegram WebApp API. Дам типы и удобный hook, чтобы не дёргать
// window.Telegram.WebApp напрямую из компонентов.
//
// В web-режиме (platform=web) методы — no-op, hook возвращает дефолты.
// В TMA-режиме — реальные вызовы.

import { useEffect, useState, useCallback } from 'react';
import { play as playSfx } from '../audio/sfx';

// Минимальная типизация Telegram WebApp (мы используем только часть API).
// Полная — в @telegram-apps/sdk-react, но для прототипа хватит.
interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      photo_url?: string;
    };
    auth_date?: number;
    hash?: string;
    // РЕФ.1: ?startapp=КОД из ссылки-инвайта.
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  showAlert: (message: string, cb?: () => void) => void;
  showConfirm: (message: string, cb?: (confirmed: boolean) => void) => void;
  openLink: (url: string) => void;
  openTelegramLink: (url: string) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export function getTelegramApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function isInTelegram(): boolean {
  const tg = getTelegramApp();
  if (!tg) return false;
  // initData может быть пустым при запуске через Menu Button
  // (в отличие от Keyboard/Inline button). Поэтому одного initData мало.
  // Признаки реального TMA-окружения:
  //   1. Непустой initData (нормальный сценарий с подписью)
  //   2. platform не 'unknown' (Telegram WebApp прописал ios/android/tdesktop/...)
  //   3. version > '6.0' (Telegram WebApp вне TG возвращает дефолт)
  if (tg.initData && tg.initData.length > 0) return true;
  if (tg.platform && tg.platform !== 'unknown') return true;
  if (tg.version && tg.version !== '6.0') return true;
  return false;
}

// Hook для получения user-данных Telegram + state colorScheme.
export function useTelegram() {
  const tg = getTelegramApp();
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>(tg?.colorScheme ?? 'dark');

  useEffect(() => {
    if (!tg) return;
    // В Telegram WebApp нет полноценного listener для colorScheme — слушаем
    // через свой event на theme_change (`themeChanged`). API передаётся через
    // window.Telegram.WebApp.onEvent — добавим минимальную обвязку, если нужно.
    // Для прототипа достаточно начального значения.
    setColorScheme(tg.colorScheme);
  }, [tg]);

  return {
    available: !!tg,
    user: tg?.initDataUnsafe?.user,
    initData: tg?.initData ?? '',
    colorScheme,
    platform: tg?.platform ?? 'unknown',
    version: tg?.version ?? '0.0',
    viewportHeight: tg?.viewportHeight ?? window.innerHeight,
    // Haptics + SFX — приятный дефолт для кнопок. Звук синтезируется в sfx.ts
    // (не зависит от Telegram → работает и в web-режиме на десктопе). Гаптика
    // зовётся в ~207 местах → звук подключается ко всем разом.
    hapticTap: useCallback(() => {
      tg?.HapticFeedback?.impactOccurred?.('light');
      playSfx('tap');
    }, [tg]),
    hapticSuccess: useCallback(() => {
      tg?.HapticFeedback?.notificationOccurred?.('success');
      playSfx('success');
    }, [tg]),
    hapticError: useCallback(() => {
      tg?.HapticFeedback?.notificationOccurred?.('error');
      playSfx('error');
    }, [tg]),
    alert: useCallback((msg: string) => {
      if (tg?.showAlert) tg.showAlert(msg);
      else alert(msg);
    }, [tg]),
    confirm: useCallback((msg: string): Promise<boolean> => {
      return new Promise((resolve) => {
        if (tg?.showConfirm) tg.showConfirm(msg, (yes) => resolve(yes));
        else resolve(window.confirm(msg));
      });
    }, [tg])
  };
}

// Hook для BackButton — показывает кнопку «назад» в шапке Telegram,
// при нажатии вызывает onBack. В web-режиме — no-op.
export function useBackButton(onBack: () => void, visible = true) {
  const tg = getTelegramApp();
  useEffect(() => {
    if (!tg?.BackButton) return;
    if (visible) {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
    } else {
      tg.BackButton.hide();
    }
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [tg, onBack, visible]);
}
